import React, { useState, useEffect, Fragment, useRef, useMemo } from 'react';
import fp, { get, isEmpty, map, replace } from 'lodash/fp';
import { useSelector, useDispatch } from 'react-redux';
import moment from 'moment';
import PropTypes from 'prop-types';
import ReactExport from 'react-export-excel';
import { Button, Icon } from 'rsuite';
import {
  getDownloadRequestForId,
  getDownloadRequestResultForId
} from '../../redux/selectors/download-request';
import {
  fetchDownloadRequest,
  fetchDownloadRequestResult
} from '../../redux/sagas/routines';
import { Status } from '../../constants/download-request';
import { fetchBlob } from '../../services/fbm-api/api-request';

const { ExcelFile } = ReactExport;
const { ExcelSheet } = ExcelFile;

const composeDataSet = (reportName, headerData, columnLabels, items) => {
  if (isEmpty(items)) return [];
  const itemsColumns = fp.keys(items[0]);
  const dataSet = [
    // Header
    {
      columns: ['', ''],
      data: [
        [
          { value: 'Report Name', style: { font: { bold: true } } },
          { value: reportName }
        ],
        ...headerData
      ]
    },
    // Items
    {
      xSteps: 0, // Will start putting cell with 1 empty cell on left most
      ySteps: 2, // will put space of 5 rows,
      columns: map(column => columnLabels[column], itemsColumns),
      data: fp.map(fp.values, items)
    }
  ];

  return dataSet;
};

function DownloadRequestExportButton({
  name,
  context,
  query,
  exportHeader,
  exportColumnLabels
}) {
  const dispatch = useDispatch();
  const fetchResultIntervalRef = useRef(null);
  const [downloadRequestId, setDownloadRequestId] = useState(false);
  const downloadBtnElem = document.getElementById('downloadBtn');
  const [isExporting, setIsExporting] = useState(false);
  const downloadRequest = useSelector(state =>
    getDownloadRequestForId(state, downloadRequestId)
  );
  const result = useSelector(state =>
    getDownloadRequestResultForId(state, downloadRequestId)
  );
  const resultItems = get('items', result);
  const isInProgress = get('status', downloadRequest) === Status.IN_PROGRESS;

  const handleExport = async () => {
    if (isEmpty(resultItems)) {
      setIsExporting(true);
      const response = await fetchBlob(`download-requests`, {
        method: 'POST',
        body: { name, context, query }
      });
      const { id } = JSON.parse(await response.text());
      setDownloadRequestId(id);
      fetchResultIntervalRef.current = setInterval(() => {
        dispatch(fetchDownloadRequest(id));
        dispatch(fetchDownloadRequestResult(id));
      }, 5000);
    } else {
      downloadBtnElem.click();
    }
  };

  const dataSet = useMemo(
    () => {
      if (isInProgress) return [];
      return composeDataSet(
        name,
        exportHeader,
        exportColumnLabels,
        resultItems
      );
    },
    [resultItems, isInProgress]
  );

  useEffect(
    () => {
      if (
        !isInProgress &&
        !isEmpty(resultItems) &&
        fetchResultIntervalRef.current
      ) {
        clearInterval(fetchResultIntervalRef.current);
        fetchResultIntervalRef.current = null;
        downloadBtnElem.click();
        setIsExporting(false);
      }
    },
    [isInProgress, resultItems]
  );

  return (
    <Fragment>
      <Button appearance="ghost" onClick={handleExport} disabled={isExporting}>
        {isExporting ? <Icon icon="spinner" pulse /> : <Icon icon="export" />}
        {isExporting ? ' Exporting...' : ' Export'}
      </Button>
      <ExcelFile
        filename={`${replace(' ', '_', name)}_${moment().format('YMMDD')}`}
        element={
          <Button id="downloadBtn" style={{ display: 'none' }}>
            Download
          </Button>
        }
      >
        <ExcelSheet dataSet={dataSet} name={name} />
      </ExcelFile>
    </Fragment>
  );
}

DownloadRequestExportButton.propTypes = {
  name: PropTypes.string.isRequired,
  context: PropTypes.number.isRequired,
  query: PropTypes.shape({}).isRequired,
  exportHeader: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.shape({})))
    .isRequired,
  exportColumnLabels: PropTypes.shape({}).isRequired
};

export default DownloadRequestExportButton;
